import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'reactstrap';
import { toast } from 'react-toastify';
import Api from '../../../util/api';
import Loading from '../../../shared/components/custom/Loading';
import FormCard from '../../../shared/components/custom/FormCard';
import { partial } from '../../../util/translation/translation';
import ConnectPeriodPicker from './ConnectPeriodPicker';
import { formatAmount, periodRange, totalsByCurrency } from '../connect';

// Completed Connect payments in a period. Clubs: a small table per receiver
// (what supporters paid, the platform's fee, what the club gets). Super-users
// (`all`): every receiver, with Stripe's fee and what the platform keeps, and
// totals per currency.
const ConnectReport = ({ all, clubNames }) => {
  const p = partial('connect');
  const [period, setPeriod] = useState(() => ({
    period: 'thisMonth',
    ...periodRange('thisMonth'),
  }));
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let current = true;
    const fetchReport = async () => {
      setLoading(true);
      try {
        const range = {
          from: period.from.toISOString(),
          to: period.to.toISOString(),
        };
        const report = all
          ? await Api.connect.getAllReport(range)
          : await Api.connect.getReport(range);
        if (current) {
          setRows(report || []);
        }
      } catch (err) {
        toast.error(
          typeof err === 'string' && err ? err : p('reportRetrievingERROR')
        );
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
    // A later period wins over a slower earlier request
    return () => {
      current = false;
    };
    // p is redefined every render
    // eslint-disable-next-line
  }, [all, period.from.valueOf(), period.to.valueOf()]);

  const missingNote = (count) =>
    count > 0 ? (
      <small className="d-block text-muted">
        {p('missingDetails', { count })}
      </small>
    ) : null;
  const clubName = (teamId) => clubNames[`${teamId}`] || `#${teamId}`;

  const clubReport = (rows || []).map((row) => (
    <FormCard key={row.receiverId} title={row.name} className="mb-3">
      <Table size="sm" className="mb-0">
        <tbody>
          <tr>
            <th>{p('reportPayments')}</th>
            <td className="text-right">{row.payments}</td>
          </tr>
          <tr>
            <th>{p('reportGross')}</th>
            <td className="text-right">
              {formatAmount(row.gross, row.currency)}
            </td>
          </tr>
          <tr>
            <th>{p('reportFee')}</th>
            <td className="text-right">
              {formatAmount(row.applicationFee, row.currency)}
            </td>
          </tr>
          <tr>
            <th>{p('reportPayout')}</th>
            <td className="text-right">
              {formatAmount(row.payout, row.currency)}
            </td>
          </tr>
        </tbody>
      </Table>
      {missingNote(row.missingDetails)}
    </FormCard>
  ));

  const amountCells = (row) => (
    <>
      <td className="text-right">{row.payments}</td>
      <td className="text-right">{formatAmount(row.gross, row.currency)}</td>
      <td className="text-right">
        {formatAmount(row.applicationFee, row.currency)}
      </td>
      <td className="text-right">
        {formatAmount(row.stripeFee, row.currency)}
      </td>
      <td className="text-right">
        {formatAmount(row.platformNet, row.currency)}
      </td>
      <td className="text-right">{formatAmount(row.payout, row.currency)}</td>
    </>
  );

  const allReport = (
    <FormCard bodyClassName="form-card__body--flush">
      <Table responsive striped className="table--roomy table--stack mb-0">
        <thead>
          <tr>
            <th>{p('club')}</th>
            <th>{p('receiver')}</th>
            <th className="text-right">{p('reportPayments')}</th>
            <th className="text-right">{p('reportGross')}</th>
            <th className="text-right">{p('reportFee')}</th>
            <th className="text-right">{p('reportStripeFee')}</th>
            <th className="text-right">{p('reportPlatformNet')}</th>
            <th className="text-right">{p('reportPayout')}</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((row) => (
            <tr key={row.receiverId}>
              <td>{clubName(row.teamId)}</td>
              <td>
                {row.name}
                {missingNote(row.missingDetails)}
              </td>
              {amountCells(row)}
            </tr>
          ))}
          {totalsByCurrency(rows || []).map((total) => (
            <tr key={`total-${total.currency}`} className="bold">
              <td colSpan={2}>
                {p('reportTotal', { currency: total.currency })}
                {missingNote(total.missingDetails)}
              </td>
              {amountCells(total)}
            </tr>
          ))}
        </tbody>
      </Table>
    </FormCard>
  );

  return (
    <>
      <Loading loading={loading} />
      <ConnectPeriodPicker value={period} onChange={setPeriod} />
      {rows && !rows.length && <p className="text-muted">{p('reportEmpty')}</p>}
      {rows?.length > 0 && (all ? allReport : clubReport)}
    </>
  );
};

ConnectReport.propTypes = {
  all: PropTypes.bool,
  clubNames: PropTypes.objectOf(PropTypes.string),
};

ConnectReport.defaultProps = {
  all: false,
  clubNames: {},
};

export default ConnectReport;
